import { toast, ToastOptions } from 'react-toastify';
import { describeAdjustment, FinishAdjustment, FINISH_LABELS, fromHaveFinish, HaveFinish } from 'utils/finishes';
import { describeLanguageAdjustment, LanguageAdjustment } from 'utils/entryDefaults';

// Default options for different toast types
const defaultOptions: ToastOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

// Success notification
export const showSuccess = (message: string, options?: ToastOptions) => {
  return toast.success(message, {
    ...defaultOptions,
    ...options,
  });
};

// Error notification
export const showError = (message: string, options?: ToastOptions) => {
  return toast.error(message, {
    ...defaultOptions,
    autoClose: 7000, // Errors stay longer
    ...options,
  });
};

// Warning notification
export const showWarning = (message: string, options?: ToastOptions) => {
  return toast.warning(message, {
    ...defaultOptions,
    autoClose: 6000,
    ...options,
  });
};

// Info notification
export const showInfo = (message: string, options?: ToastOptions) => {
  return toast.info(message, {
    ...defaultOptions,
    ...options,
  });
};

// Loading notification (returns id to update later)
export const showLoading = (message: string = 'Loading...', options?: ToastOptions) => {
  return toast.loading(message, {
    ...defaultOptions,
    autoClose: false,
    closeOnClick: false,
    ...options,
  });
};

// Update existing toast
export const updateToast = (toastId: string | number, update: {
  render: string;
  type?: 'success' | 'error' | 'warning' | 'info' | 'default';
  isLoading?: boolean;
  autoClose?: number | false;
}) => {
  toast.update(toastId, {
    ...update,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

// Promise-based toast (for async operations)
export const showPromise = <T = any>(
  promise: Promise<T>,
  messages: {
    pending: string;
    success: string;
    error: string;
  },
  options?: ToastOptions
) => {
  return toast.promise(
    promise,
    {
      pending: messages.pending,
      success: messages.success,
      error: messages.error,
    },
    {
      ...defaultOptions,
      ...options,
    }
  );
};

// Dismiss specific toast
export const dismissToast = (toastId?: string | number) => {
  if (toastId) {
    toast.dismiss(toastId);
  } else {
    toast.dismiss(); // Dismiss all if no id provided
  }
};

// Check if a toast is active
export const isToastActive = (toastId: string | number) => {
  return toast.isActive(toastId);
};

// Custom styled notifications for specific use cases
export const showCardAddedSuccess = (cardName: string, quantity: number) => {
  return showSuccess(`${quantity} ${cardName} added to your Want List`);
};

export const showCardAddedToHaveListSuccess = (cardName: string, quantity: number) => {
  return showSuccess(`${quantity} ${cardName} added to your Have List`);
};

export const showCardUpdatedWarning = (cardName: string, sets?: string[]) => {
  const setsInfo = sets && sets.length > 0 
    ? ` with ${sets.length > 3 ? `${sets.length} sets` : sets.join(', ')}`
    : '';
  
  return showWarning(`⚠️ Updated existing want list entry for ${cardName}${setsInfo}`);
};

export const showAuthError = (message: string = 'Please log in to continue') => {
  return showError(`🔒 ${message}`, {
    autoClose: 5000,
  });
};

export const showNetworkError = (message: string = 'Network error. Please check your connection.') => {
  return showError(`🌐 ${message}`, {
    autoClose: 8000,
  });
};

export const showValidationError = (errors: string[]) => {
  const message = errors.length > 1 
    ? `Validation errors:\n${errors.join('\n')}`
    : errors[0];
  
  return showError(message, {
    autoClose: 8000,
  });
};

// Batch notifications (useful for multiple operations)
export const showBatchSuccess = (successCount: number, totalCount: number, operation: string) => {
  if (successCount === totalCount) {
    return showSuccess(`✓ All ${totalCount} ${operation} completed successfully`);
  } else if (successCount > 0) {
    return showWarning(`⚠️ ${successCount} of ${totalCount} ${operation} completed`);
  } else {
    return showError(`✗ All ${operation} failed`);
  }
};

/**
 * D-7 toast for a single add or edit whose stored finish differs from the
 * requested one. Info, not warning: the backend did the right thing and is
 * telling the user so.
 */
export const showFinishAdjustment = (adjustment: FinishAdjustment | null | undefined) => {
  const text = describeAdjustment(adjustment);
  if (text) showInfo(text, { autoClose: 8000 });
};

/** Info toast when a finish change folded the row into an existing copy of that finish. */
export const showFinishMerge = (finish: HaveFinish) => {
  showInfo(`Merged into your existing ${FINISH_LABELS[fromHaveFinish(finish)]} copy.`, { autoClose: 8000 });
};

/** Info toast when a Have List add could not be stored in the requested language. */
export const showLanguageAdjustment = (adjustment: LanguageAdjustment | null | undefined) => {
  const text = describeLanguageAdjustment(adjustment);
  if (text) showInfo(text, { autoClose: 8000 });
};
